import React from 'react';
import Icon from '../../../components/AppIcon';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const PermitPerformance = ({ data, loading }) => {
  const statusColors = {
    'submitted': 'var(--color-info)',
    'submission': 'var(--color-info)',
    'pending': 'var(--color-info)',
    'under_review': 'var(--color-warning)',
    'under review': 'var(--color-warning)',
    'in_review': 'var(--color-warning)',
    'approved': 'var(--color-success)',
    'rejected': 'var(--color-error)',
    'expired': 'var(--color-error)'
  };

  const statusLabels = {
    'submitted': 'Submitted',
    'submission': 'Submission',
    'pending': 'Pending',
    'under_review': 'Under Review',
    'under review': 'Under Review',
    'in_review': 'In Review',
    'approved': 'Approved',
    'rejected': 'Rejected',
    'expired': 'Expired'
  };

  const chartData = data?.statusBreakdown?.map(item => ({
    name: statusLabels?.[item?.status] || item?.status,
    value: item?.count,
    status: item?.status
  })) || [];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-semibold text-foreground mb-1">{payload?.[0]?.name}</p>
          <p className="text-xs text-muted-foreground">
            Count: {payload?.[0]?.value}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6">
      <div className="flex items-center justify-center h-64">
        <Icon name="FileCheck" size={32} className="animate-spin text-primary" />
      </div>
      <div className="flex items-center gap-2 mb-6">
        <Icon name="FileCheck" size={24} className="text-primary" />
        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground">Permit Performance</h3>
          <p className="text-xs text-muted-foreground">Approval rates and processing metrics</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Key Metrics */}
        <div className="space-y-4">
          <div className="bg-success/5 border border-success/20 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Approval Rate</span>
              <Icon name="CheckCircle" size={20} className="text-success" />
            </div>
            <p className="text-3xl font-bold text-success">{data?.approvalRate?.toFixed(1)}%</p>
            <div className="mt-2 bg-muted rounded-full h-2 overflow-hidden">
              <div 
                className="bg-success h-full transition-all duration-500"
                style={{ width: `${data?.approvalRate}%` }}
              />
            </div>
          </div>

          <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Avg Processing Time</span>
              <Icon name="Clock" size={20} className="text-warning" />
            </div>
            <p className="text-3xl font-bold text-warning">{data?.avgProcessingTime} <span className="text-lg">days</span></p>
            <p className="text-xs text-muted-foreground mt-1">From submission to decision</p>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Total Permits</span>
              <Icon name="FileText" size={20} className="text-primary" />
            </div>
            <p className="text-3xl font-bold text-primary">
              {data?.statusBreakdown?.reduce((sum, item) => sum + (item?.count || 0), 0)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Across all statuses</p>
          </div>
        </div>

        {/* Status Breakdown Chart */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">Status Breakdown</h4>
          {chartData?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Icon name="FileCheck" size={48} className="mb-2 opacity-50" />
              <p className="text-sm">No permit data available</p>
            </div>
          ) : (
            <div className="w-full h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="var(--color-primary)"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}
                  >
                    {chartData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={statusColors?.[entry?.status] || 'var(--color-muted)'} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    wrapperStyle={{ fontSize: '11px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermitPerformance;